import React, { useEffect, useState } from 'react';
import { Helmet } from "react-helmet";
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box,
    Typography,
    Paper,
    Button,
    Container,
    Alert,
    AlertTitle
} from '@material-ui/core';
import headerImage from '../../img/header_image.jpg';

import { getThreadList } from '../../actions/threadActions';
import Spinner from '../common/Spinner';
import ThreadList from './ThreadList';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
    },
    pageHeader: {
        height: '30rem',
        backgroundImage: `url(${headerImage})`,
        backgroundSize: 'cover',
        marginBottom: '5rem',
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            height: '20rem',
        }
    },
    pageTitle: {
        color: 'white',
        display: 'inline-block',
        margin: 'auto',
        fontSize: '4rem',
        padding: '2rem',
        backgroundColor: 'rgba(10, 10, 10, 0.8)',
        borderRadius: '10px',
        border: '1px solid black',
        [theme.breakpoints.down('sm')]: {
            fontSize: '2.6rem',
            padding: '1.5rem',
        }
    },
    container: {
        margin: '0 5rem',
        [theme.breakpoints.down('md')]: {
            margin: '0 2.5rem',
        },
        [theme.breakpoints.down('sm')]: {
            margin: '0 1rem',
        }
    },
    infoBox: {
        backgroundColor: '#ebf7fe',
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: '10px',
        padding: '2rem',
        overflowY: 'auto',
        marginBottom: '2rem',
    },
    infoText: {
        marginBottom: '1.5rem',
        fontSize: '1.6rem',
        lineHeight: 1.6,
    },
    dangerText: {
        color: theme.palette.error.main,
        fontWeight: 'bold',
        fontSize: '1.6rem',
        marginBottom: '1.5rem',
    },
    infoTextBlue: {
        color: theme.palette.info.main,
        fontSize: '1.6rem',
        marginBottom: '1.5rem',
    },
    buttonContainer: {
        textAlign: 'center',
        margin: '2rem 0',
    },
    createButton: {
        fontSize: '1.6rem',
        padding: '1rem 2rem',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        textDecoration: 'none',
        display: 'inline-block',
        borderRadius: '4px',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
    }
}));

const ThreadHome = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [created, setCreated] = useState(false);
    const threadList = useSelector(state => state.thread.threadList);
    const user = useSelector(state => state.user.currentUser);
    const loading = useSelector(state => state.common.loading);

    useEffect(() => {
        dispatch(getThreadList());
    }, [dispatch])

    useEffect(() => {
        if (threadList && user) {
            if (threadList.findIndex(thread => thread.user._id === user._id) >= 0) {
                setCreated(true);
            }
            else {
                setCreated(false);
            }
        }
    }, [threadList, user, setCreated])

    return (
        <div className={classes.root}>
            <Helmet>
                <title>自由投稿板</title>
                <meta name="description" content="企業アカウント、求職アカウント共にご利用可能な簡易情報投稿機能です。全ての投稿は30日更新が無いと自動的に消去されます。" />
            </Helmet>

            <Box className={classes.pageHeader}>
                <Typography variant="h1" className={classes.pageTitle}>
                    自由投稿板
                </Typography>
            </Box>

            <Box className={classes.container}>
                <Paper className={classes.infoBox} elevation={2}>
                    {user && user.profile.user_type === "Business" && (
                        <Typography className={classes.dangerText}>
                            企業アカウントにて求人投稿をする場合は、右上『メニュー』⇨求人投稿•編集より行ってください。自由投稿板に投稿された内容は求人検索に表示されません。
                        </Typography>
                    )}

                    <Typography className={classes.infoText}>
                        企業アカウント、求職アカウント共にご利用可能な簡易情報投稿機能です。
                    </Typography>

                    <Typography className={classes.infoTextBlue}>
                        ※現在は特に書き込む内容に制限はございません。ただし利用規約に反するような書き込みがあった場合には告知なく削除させて頂く事もございます。
                    </Typography>

                    <Typography className={classes.infoTextBlue}>
                        ※１アカウントにつき1つだけ投稿できます。別の内容の投稿をする際は、既存の投稿を削除して新規に作成してください。
                    </Typography>

                    <Typography className={classes.infoTextBlue}>
                        ※全ての投稿は30日更新が無いと自動的に消去されます。
                    </Typography>
                </Paper>

                {!user || created ? null : (
                    <Box className={classes.buttonContainer}>
                        <Link to="/thread/edit" className={classes.createButton}>
                            新しい情報を投稿する
                        </Link>
                    </Box>
                )}

                {loading ? <Spinner cover={true} /> : <ThreadList threads={threadList} />}
            </Box>
        </div>
    )
}

export default ThreadHome
